import { FlightPathXCognitiveLoad as FlightPathXCognitiveLoadComponent } from "heat-next";

import { useSessionDetailsContext } from "@/context/session-details/session-details";

import { ComponentWrapper } from "@/pages/shared/shared.styles";

import ContentLoader from "@/components/content-loader/content-loader";
import EmptyState from "@/components/empty-state/empty-state";
import { EmptyStateType } from "@/components/empty-state/empty-state.types";

interface FlightPathXCognitiveLoadProps {
    isInsideModal?: boolean;
    subDimensionsData?: any;
}

const FlightPathXCognitiveLoad = ({
    isInsideModal,
    subDimensionsData,
}: FlightPathXCognitiveLoadProps) => {
    const {
        customChartsData,
        isErrorCustomCharts,
        isFetchingCustomCharts,
        combinedCustomChartsData,
        isErrorCombinedCustomCharts,
        isFetchingCombinedCustomCharts,
        flightCircuitData,
        isFetchingFlightCircuit,
        isErrorFlightCircuit,
    } = useSessionDetailsContext();

    //CognitiveLoad
    const customCharts = isInsideModal
        ? subDimensionsData?.flat().filter((item: any) => item?.chartType === "Gauge")
        : customChartsData;

    //CognitiveLoad
    const combinedCustomCharts = isInsideModal
        ? subDimensionsData?.flat().filter((item: any) => item?.chartType === "Timeline")
        : combinedCustomChartsData;

    //FlightPath
    const flightCircuit = isInsideModal
        ? subDimensionsData?.flat().filter((item: any) => item?.chartType === "FlightPath")
        : flightCircuitData;

    //Create a combined cognitiveLoad dataset to animate the cognitive load gauge
    //Get the cognitiveLoad data from combinedCustomChartsData
    const cognitiveTimelineData = (combinedCustomCharts || []).reduce(
        (acc: any[], customData: any) => {
            const matches = (customData.datasets || []).filter(
                (item: any) => item.name === "CognitiveLoad",
            );

            return acc.concat(matches);
        },
        [],
    );

    //Build an array of timestamped score objects to insert into the combined cognitive load data
    const cognitiveTimeData = (cognitiveTimelineData?.[0]?.data || []).map((item: any) => ({
        Score: Math.ceil(item.Data),
        DateTime: item.DateTime,
    }));

    const combinedCognitiveLoadData = { ...customCharts?.[0], datasets: cognitiveTimeData };

    //Prepare data for FlightPathXCognitiveLoad component per circuit
    const flightPathXCognitiveLoadData = flightCircuit?.map((circuit: any) => {
        return {
            title: circuit.title || "Flight Path",
            flightPathData: circuit,
            cognitiveLoadData: combinedCognitiveLoadData, //write function to accept multiple circuits when used outside modal
        };
    });

    const isFlightCircuitDataEmptyState =
        !isErrorFlightCircuit &&
        (!flightPathXCognitiveLoadData || flightPathXCognitiveLoadData?.pathData?.length === 0);

    const isCustomChartsDataEmptyState =
        !isErrorCustomCharts && (!customCharts || customCharts?.length === 0);

    const isCombinedCustomChartsDataEmptyState =
        !isErrorCombinedCustomCharts &&
        (!combinedCustomCharts || combinedCustomCharts?.length === 0);

    const isEmptyState = () => {
        if (isInsideModal) {
            return subDimensionsData?.every((v: any) => v === undefined);
        } else if (
            isFlightCircuitDataEmptyState ||
            isCustomChartsDataEmptyState ||
            isCombinedCustomChartsDataEmptyState
        ) {
            return true;
        }
    };

    if (isFetchingFlightCircuit || isFetchingCustomCharts || isFetchingCombinedCustomCharts) {
        return <ContentLoader height="28.875rem" />;
    }

    if (
        (!isInsideModal && isErrorCustomCharts && !isFetchingCustomCharts) ||
        (!isInsideModal && isErrorCombinedCustomCharts && !isFetchingCombinedCustomCharts) ||
        (!isInsideModal && isErrorFlightCircuit && !isFetchingFlightCircuit)
    ) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="error-state:title"
                    emptyStateType={EmptyStateType.Error}
                    minHeight="18.1875rem"
                />
            </ComponentWrapper>
        );
    }

    if (isEmptyState()) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="session-detail:no-flight-path-data"
                    emptyStateType={EmptyStateType.Charts}
                    minHeight="25.875rem"
                />
            </ComponentWrapper>
        );
    }

    if (isFetchingFlightCircuit || isFetchingCustomCharts || isFetchingCombinedCustomCharts) {
        return <ContentLoader height={"26.4375rem"} />;
    }

    return (
        <FlightPathXCognitiveLoadComponent
            dualPlaybackProps={{
                rowGap: "0.5rem",
                leftPercent: 50,
            }}
            isInsideModal={isInsideModal}
            data={flightPathXCognitiveLoadData}
        />
    );
};

export default FlightPathXCognitiveLoad;
